import { asyncPipe } from '@helo/utils'
import { ifThen } from '@helo/utils'
import { create } from 'zustand'

import { zustandHmrFix } from '../utils'
import { AppStatusSlice, appStatusSlice } from './appStatus/appStatusSlice'
import { AppStatusSliceActions } from './appStatus/appStatusSlice.actions'
import * as App from './appStore.parts'
import { setupAxiosInterceptors } from './appStore.parts'
import { AuthSlice, authSlice } from './auth/authSlice'
import { AuthSliceActions } from './auth/authSliceActions'
import {
  BrowserDocumentTitleSlice,
  browserDocumentTitleSlice,
} from './browserDocumentTitle/browserDocumentTitleSlice'
import { CmsSlice, cmsSlice } from './cms/cmsSlice'
import { CmsSliceActions } from './cms/cmsSlice.actions'
import { ConfigSlice, configSlice } from './config/configSlice'
import { ConfigSliceActions } from './config/configSlice.actions'
import {
  NotificationsSlice,
  notificationsSlice,
} from './notifications/notificationSlice'
import { NotificationSliceActions } from './notifications/notificationSlice.actions'
import {
  PersistenceSlice,
  persistenceSlice,
} from './persistence/persistenceSlice'
import { PersistenceSliceActions } from './persistence/persistenceSlice.actions'
import { QuerySlice, querySlice } from './queryStore/querySlice'
import { RouterSlice, RouterSliceActions, routerSlice } from './router'
import {
  UserSettingsSlice,
  userSettingsSlice,
} from './userSettings/userSettingsSlice'
import { UserSettingsSliceActions } from './userSettings/userSettingsSlice.actions'
import { ViewSlice, viewSlice } from './view/viewSlice'

export type AppState = {
  status: 'initial' | 'initializing' | 'started'
}

export type AppStore = AppState & {
  initialize: () => Promise<void>
} & ConfigSlice &
  AppStatusSlice &
  AuthSlice &
  PersistenceSlice &
  BrowserDocumentTitleSlice &
  RouterSlice &
  NotificationsSlice &
  QuerySlice &
  ViewSlice &
  UserSettingsSlice &
  CmsSlice

export const useAppStore = create<AppStore>((set, get, store) => ({
  status: 'initial',
  ...configSlice(set, get, store),
  ...appStatusSlice(set, get, store),
  ...authSlice(set, get, store),
  ...persistenceSlice(set, get, store),
  ...browserDocumentTitleSlice(set, get, store),
  ...routerSlice(set, get, store),
  ...notificationsSlice(set, get, store),
  ...querySlice(set, get, store),
  ...viewSlice(set, get, store),
  ...userSettingsSlice(set, get, store),
  ...cmsSlice(set, get, store),
  initialize: async () => {
    await asyncPipe(
      get(),
      ConfigSliceActions.loadAppConfig(),
      store => {
        set(store)
        return get()
      },
      AppStatusSliceActions.ensureApiAvailable(),
      store => {
        set(store)
        return get()
      },
      AuthSliceActions.initializeAuth(),
      (store: AppStore) => {
        set(store)
        return get()
      },
      AuthSliceActions.fetchCurrentUser(),
      UserSettingsSliceActions.loadUserSettings(),
      CmsSliceActions.retrieveCmsConfigs(),
      PersistenceSliceActions.initializePersistence(),
      PersistenceSliceActions.loadPersistedItems(), // doesn't load url persistence for the upcoming page due to redirect no having occurred yet.
      ifThen(
        store => store.auth.isLoggingIn,
        PersistenceSliceActions.triggerPersistenceEvent('login'),
      ),
      (store: AppStore) => ({
        ...store,
        auth: {
          ...store.auth,
          isLoggingIn: false,
        },
      }),
      setupAxiosInterceptors(),
      RouterSliceActions.initializeRouter(),
      CmsSliceActions.initializeCms(get),
      App.redirectToAuthProvidedUrl(),
      // set started status in state.
      store => ({
        ...store,
        status: 'started' as const,
      }),
      store => {
        set(store)
        return get()
      },
      // Putting at the end due to blocking.
      store => {
        // Need unawaited async call to prevent blocking setup.
        NotificationSliceActions.startPolling()(get()).then(
          ({ notifications }) =>
            // others things may be set, so only updating notifications.
            set(store => ({ ...store, notifications })),
        )
        CmsSliceActions.startSchemaWatch(get)
      },
    )
  },
}))

zustandHmrFix('appStore', useAppStore)
